import { useUserContext } from '@/api/hooks/useUserContext';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useT } from '@/i18n';
import {
  Block,
  BlockTitle,
  Icon,
  Link,
  List,
  ListItem,
  Navbar,
  NavRight,
  Page,
  Popup,
  Searchbar,
  Subnavbar,
  f7,
} from 'framework7-react';
import { useState } from 'react';

/**
 * Switch the active restaurant in place - pick one from the franchises the user
 * can access (from `/auth/me`); the store re-points + re-bootstraps. Picking a
 * row IS the action, so there's no Save button.
 *
 * DESKTOP ONLY: a centred modal Popup with a search field to filter a long,
 * franchise-grouped list. Only the desktop layout mounts this (SettingsPage,
 * Sidebar); on phones the picker is a full pushed page (RestaurantSwitcherPage).
 */
export default function RestaurantSwitcherSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  /** Legacy anchor from the old popover - the modal centres itself, so unused. */
  anchorEl?: HTMLElement | null;
}) {
  const t = useT();
  const selectedRestaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const [query, setQuery] = useState('');

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  return (
    <Popup className="restaurant-switcher-popup" opened={open} onPopupClosed={handleClose}>
      <Page name="restaurant-switcher" className="page-with-subnavbar">
        <Navbar title={t.settings.switchRestaurant}>
          <NavRight>
            <Link popupClose>{t.close}</Link>
          </NavRight>
          <RestaurantSearchbar query={query} onQuery={setQuery} />
        </Navbar>
        <RestaurantSwitcherList
          selectedRestaurantId={selectedRestaurantId}
          query={query}
          onPick={(franchiseId, restaurantId, name) => {
            if (restaurantId !== selectedRestaurantId) {
              applyRestaurantSwitch(franchiseId, restaurantId, name);
            }
            f7.popup.close();
          }}
        />
      </Page>
    </Popup>
  );
}

export function RestaurantSearchbar({
  query,
  onQuery,
}: {
  query: string;
  onQuery: (query: string) => void;
}) {
  const t = useT();
  return (
    <Subnavbar inner={false}>
      <Searchbar
        customSearch
        backdrop={false}
        disableButton={false}
        placeholder={t.settings.searchRestaurants}
        value={query}
        onChange={(e) => {
          if (e.target instanceof HTMLInputElement) onQuery(e.target.value);
        }}
        onClickClear={() => onQuery('')}
      />
    </Subnavbar>
  );
}

/**
 * Re-point the active restaurant + confirm with a toast. The single source of
 * truth shared by the desktop modal and the phone switcher page so both switch
 * identically. Reads the store imperatively so non-hook callers (a page's pick
 * handler deferred to the next frame) can use it too.
 */
export function applyRestaurantSwitch(franchiseId: string, restaurantId: string, name: string) {
  useRestaurantStore.getState().selectRestaurant(franchiseId, restaurantId);
  f7.toast.show({ text: `Switched to ${name}`, closeTimeout: 2000 });
}

function humanizeRole(role: string | null): string {
  // null = franchise-wide access (partner-group member or system admin) rather
  // than a specific restaurant role.
  if (!role) return 'Full access';
  return role.charAt(0) + role.slice(1).toLowerCase().replace(/_/g, ' ');
}

export function RestaurantSwitcherList({
  selectedRestaurantId,
  onPick,
  query = '',
}: {
  selectedRestaurantId: string | null;
  onPick: (franchiseId: string, restaurantId: string, name: string) => void;
  /** Optional filter: a franchise whose name matches keeps all its restaurants;
   * otherwise only the restaurants whose name matches are shown. */
  query?: string;
}) {
  const t = useT();
  // Prefer the freshly-fetched context; fall back to the store's copy (set on
  // bootstrap) so the list still renders if the query hasn't resolved yet.
  const { data: ctx } = useUserContext();
  const storeCtx = useRestaurantStore((s) => s.userContext);

  const workers = (ctx ?? storeCtx)?.workers ?? [];

  const q = query.trim().toLowerCase();
  const shown = q
    ? workers
        .map((w) => ({
          ...w,
          restaurants: w.franchiseName.toLowerCase().includes(q)
            ? w.restaurants
            : w.restaurants.filter((r) => r.restaurantName.toLowerCase().includes(q)),
        }))
        .filter((w) => w.restaurants.length > 0)
    : workers;

  if (workers.length === 0) {
    return (
      <Block className="text-align-center text-color-gray" style={{ paddingTop: 40 }}>
        No restaurants available.
      </Block>
    );
  }

  if (shown.length === 0) {
    return (
      <Block className="text-align-center text-color-gray" style={{ paddingTop: 40 }}>
        {t.settings.noRestaurantsMatch}
      </Block>
    );
  }

  return (
    <>
      {shown.map((w) => (
        <div key={w.franchiseId}>
          <BlockTitle>{w.franchiseName}</BlockTitle>
          <List dividers strongIos strongMd insetIos insetMd>
            {w.restaurants.map((r) => {
              const isCurrent = r.restaurantId === selectedRestaurantId;
              return (
                <ListItem
                  key={r.restaurantId}
                  link="#"
                  noChevron
                  title={r.restaurantName}
                  footer={humanizeRole(r.role)}
                  onClick={() => onPick(w.franchiseId, r.restaurantId, r.restaurantName)}
                >
                  {isCurrent ? (
                    <Icon slot="after" f7="checkmark_alt" style={{ color: '#34c759' }} />
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        </div>
      ))}
    </>
  );
}
